/**
 * S24 — Reports · Scheduled Reports (`/reports/scheduled`).
 *
 * Wave 30 — Phase 3b. Migrated off `useTenantData().scheduledReports`
 * onto the `/api/scheduled-reports` REST endpoint via `useEntityList()`.
 */
import React from "react";
import { CalendarClock, MailCheck, BellRing } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

export default function ScheduledReports() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    items: scheduledReports,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntityList("/api/scheduled-reports");

  if (loading && scheduledReports.length === 0) {
    return <Skeleton />;
  }
  if (error) {
    return <ErrorView message={error.message} onRetry={refetch} />;
  }
  if (isCustomer && !scheduledReports.length) {
    return <Empty userName={user?.name?.split(" ")[0]} />;
  }

  const delivered = scheduledReports.filter((s) => s.lastStatus === "delivered").length;

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="scheduled-reports"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Reports · Scheduled</p>
          <h1 className="text-display-md text-ink-primary mt-1">Scheduled Reports</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Recurring delivery to Email, Slack, Teams or SFTP — every delivery is
            ALGOS-signed.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="scheduled-reports-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KpiTile
          label="Active schedules"
          value={scheduledReports.length.toString()}
          delta="cron tracked"
          deltaTone="neutral"
          hint="auto-recurring"
          icon={CalendarClock}
          tone="tralenex"
          testId="sr-kpi-active"
        />
        <KpiTile
          label="Delivered (last run)"
          value={delivered.toString()}
          delta={delivered === scheduledReports.length ? "all green" : `${scheduledReports.length - delivered} pending`}
          deltaTone={delivered === scheduledReports.length ? "up" : "down"}
          hint="zero delivery failures"
          icon={MailCheck}
          tone="low"
          testId="sr-kpi-delivered"
        />
        <KpiTile
          label="Channels"
          value="Email · Slack · Teams · SFTP"
          delta="multi-channel"
          deltaTone="neutral"
          hint="signed payloads"
          icon={BellRing}
          tone="gold"
          testId="sr-kpi-channels"
        />
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="sr-table-panel">
        <table className="w-full text-sm" data-testid="sr-table">
          <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
            <tr>
              {["ID", "Name", "Frequency", "Next run", "Recipients", "Channel", "Last status", "Owner"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scheduledReports.map((s) => (
              <tr
                key={s.id}
                className="border-t border-border-subtle hover:bg-inset/30"
                data-testid={`sr-row-${s.id}`}
              >
                <td className="px-3 py-2 font-mono text-meta text-ink-secondary whitespace-nowrap">
                  {s.id}
                </td>
                <td className="px-3 py-2 text-ink-primary font-medium">{s.name}</td>
                <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">
                  {s.frequency}
                </td>
                <td className="px-3 py-2 text-meta font-mono text-ink-secondary tabular-nums whitespace-nowrap">
                  {s.nextRun}
                </td>
                <td className="px-3 py-2 text-meta text-ink-secondary">
                  {(s.recipients || []).join(" · ")}
                </td>
                <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">
                  {s.channel}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span
                    className={cn(
                      "inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]",
                      s.lastStatus === "delivered"
                        ? "bg-risk-low/10 text-risk-low"
                        : "bg-gold-300/15 text-gold-700",
                    )}
                  >
                    {s.lastStatus}
                  </span>
                </td>
                <td className="px-3 py-2 text-ink-primary whitespace-nowrap text-sm">
                  {s.owner}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="scheduled-reports-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[300px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="scheduled-reports-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Reports · Scheduled</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load scheduled reports</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="scheduled-reports-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden"
      data-testid="sr-customer-empty"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Reports · Scheduled</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 text-[15px]">
          No schedules yet. Pin any generated report to a cadence and it&rsquo;ll
          auto-deliver here.
        </p>
      </div>
    </div>
  );
}
